/**
 * Personal state dashboard and water, sleep, and check-in forms. Loads trajectory plus recent logs
 * in parallel, converts local datetime inputs for API writes, and refreshes displayed history after
 * recording observations.
 *
 * @module apps/web/src/pages/StatePage
 */

import { useEffect, useEffectEvent, useMemo, useState } from 'react';
import { Activity, BedDouble, Brain, Droplets, Gauge, Loader2, Moon, Plus, Sparkles } from 'lucide-react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import type {
  SleepLog,
  StateCheckIn,
  StateTrajectory,
  WaterLog,
  WaterUnit,
} from '@fluxability/types';

/** Supported water-log unit labels, preserving the unit transmitted with each amount. */
const unitLabels: Record<WaterUnit, string> = {
  glass: 'glass',
  bottle: 'bottle',
  oz: 'oz',
  ml: 'ml',
};

/** Selected state-log form: check-in, hydration, or sleep. */
type ActiveLogger = 'check-in' | 'water' | 'sleep';

/**
 * Personal state dashboard and water, sleep, and check-in forms. Loads trajectory plus recent logs
 * in parallel, converts local datetime inputs for API writes, and refreshes displayed history after
 * recording observations.
 */
export function StatePage() {
  const { toast } = useToast();
  const [activeLogger, setActiveLogger] = useState<ActiveLogger>('check-in');
  const [trajectory, setTrajectory] = useState<StateTrajectory | null>(null);
  const [waterLogs, setWaterLogs] = useState<WaterLog[]>([]);
  const [sleepLogs, setSleepLogs] = useState<SleepLog[]>([]);
  const [checkIns, setCheckIns] = useState<StateCheckIn[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const [waterAmount, setWaterAmount] = useState('1');
  const [waterUnit, setWaterUnit] = useState<WaterUnit>('glass');
  const [hydrationFeeling, setHydrationFeeling] = useState('6');
  const [waterNote, setWaterNote] = useState('');

  const [sleepStart, setSleepStart] = useState(defaultSleepStart());
  const [sleepEnd, setSleepEnd] = useState(defaultSleepEnd());
  const [sleepQuality, setSleepQuality] = useState('7');
  const [sleepRestfulness, setSleepRestfulness] = useState('7');
  const [sleepInterruptions, setSleepInterruptions] = useState('0');
  const [wakeP, setWakeP] = useState('6');
  const [wakeRj, setWakeRj] = useState('6');
  const [sleepNote, setSleepNote] = useState('');

  const [productivityEnergy, setProductivityEnergy] = useState('6');
  const [rejuvenationEnergy, setRejuvenationEnergy] = useState('6');
  const [resistance, setResistance] = useState('4');
  const [checkHydration, setCheckHydration] = useState('6');
  const [emotion, setEmotion] = useState('');
  const [bodyState, setBodyState] = useState('');
  const [checkInText, setCheckInText] = useState('');

  const todayWindow = useMemo(() => {
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    const end = new Date(start);
    end.setDate(end.getDate() + 1);
    return { start: start.toISOString(), end: end.toISOString() };
  }, []);

  // loadState reads the latest window and filters each time it runs.
  const reloadState = useEffectEvent(() => {
    void loadState();
  });

  useEffect(() => {
    reloadState();
  }, []);

  useEffect(() => {
    const refreshState = () => {
      reloadState();
    };
    window.addEventListener('flexiplanner-state-updated', refreshState);
    return () => window.removeEventListener('flexiplanner-state-updated', refreshState);
  }, [todayWindow.start, todayWindow.end]);

  const loadState = async () => {
    setIsLoading(true);
    try {
      const [trajectoryRes, waterRes, sleepRes, checkInRes] = await Promise.all([
        api.state.getTrajectory(todayWindow.start, todayWindow.end),
        api.state.listWater(todayWindow.start, todayWindow.end),
        api.state.listSleep(5),
        api.state.listCheckIns(6),
      ]);

      if (trajectoryRes.success && trajectoryRes.data) setTrajectory(trajectoryRes.data);
      if (waterRes.success && waterRes.data) setWaterLogs(waterRes.data);
      if (sleepRes.success && sleepRes.data) setSleepLogs(sleepRes.data);
      if (checkInRes.success && checkInRes.data) setCheckIns(checkInRes.data);
    } finally {
      setIsLoading(false);
    }
  };

  const logWater = async (amount = waterAmount, unit = waterUnit) => {
    setIsSaving(true);
    try {
      const response = await api.state.logWater({
        amount: Number(amount),
        unit,
        hydration_feeling: toOptionalNumber(hydrationFeeling),
        note: waterNote || null,
      });
      if (!response.success) throw new Error(response.error?.message || 'Water log failed');
      setWaterNote('');
      toast({ title: 'Water logged', description: `${amount} ${unitLabels[unit]} added.` });
      await loadState();
    } catch (error) {
      toast({
        title: 'Water log failed',
        description: error instanceof Error ? error.message : 'Try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const logSleep = async () => {
    setIsSaving(true);
    try {
      const response = await api.state.logSleep({
        sleep_start: toIsoFromLocalInput(sleepStart),
        sleep_end: toIsoFromLocalInput(sleepEnd),
        quality: toOptionalNumber(sleepQuality),
        restfulness: toOptionalNumber(sleepRestfulness),
        interruptions: toOptionalNumber(sleepInterruptions),
        wake_productivity_energy: toOptionalNumber(wakeP),
        wake_rejuvenation_energy: toOptionalNumber(wakeRj),
        note: sleepNote || null,
      });
      if (!response.success) throw new Error(response.error?.message || 'Sleep log failed');
      setSleepNote('');
      toast({ title: 'Sleep logged', description: 'Wake-state baseline updated.' });
      await loadState();
    } catch (error) {
      toast({
        title: 'Sleep log failed',
        description: error instanceof Error ? error.message : 'Check the time window.',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const logCheckIn = async () => {
    setIsSaving(true);
    try {
      const response = await api.state.logCheckIn({
        raw_text: checkInText || null,
        productivity_energy: toOptionalNumber(productivityEnergy),
        rejuvenation_energy: toOptionalNumber(rejuvenationEnergy),
        resistance: toOptionalNumber(resistance),
        hydration_feeling: toOptionalNumber(checkHydration),
        emotion_label: emotion || null,
        body_state: bodyState || null,
      });
      if (!response.success) throw new Error(response.error?.message || 'Check-in failed');
      setCheckInText('');
      toast({ title: 'State logged', description: 'Trajectory baseline updated.' });
      await loadState();
    } catch (error) {
      toast({
        title: 'Check-in failed',
        description: error instanceof Error ? error.message : 'Try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const hydrationPercent = Math.round((trajectory?.hydration.progress ?? 0) * 100);

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">State trajectory</h1>
          <p className="text-sm text-muted-foreground">P energy, RJ energy, hydration, sleep, resistance.</p>
        </div>
        <Button variant="outline" onClick={loadState} disabled={isLoading} className="w-full sm:w-auto">
          {isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Activity className="mr-2 h-4 w-4" />}
          Refresh
        </Button>
      </div>

      <section className="grid gap-3 lg:grid-cols-[1.05fr_1.45fr]">
        <Card className="overflow-hidden">
          <CardHeader className="border-b pb-4">
            <CardTitle className="flex items-center gap-2 text-base">
              <Gauge className="h-4 w-4 text-cyan-500" />
              Current vector
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 p-4">
            <div className="grid grid-cols-2 gap-3">
              <MetricTile label="P" value={trajectory?.energy.productivity} tone="cyan" />
              <MetricTile label="RJ" value={trajectory?.energy.rejuvenation} tone="emerald" />
              <MetricTile label="Water" value={`${hydrationPercent}%`} tone="blue" />
              <MetricTile label="Sleep" value={formatSleep(trajectory?.sleep?.duration_minutes)} tone="amber" />
            </div>
            <div className="rounded-md border bg-muted/30 p-3 text-sm leading-6">
              {trajectory?.guidance || 'Loading current state.'}
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-muted">
              <div
                className="h-full rounded-full bg-[linear-gradient(90deg,#06b6d4,#22c55e,#f59e0b)]"
                style={{ width: `${Math.max(4, Math.min(100, hydrationPercent))}%` }}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="border-b pb-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <CardTitle className="flex items-center gap-2 text-base">
                <Sparkles className="h-4 w-4 text-emerald-500" />
                Log state
              </CardTitle>
              <div className="grid grid-cols-3 gap-1 rounded-md border bg-muted/30 p-1">
                <LoggerButton active={activeLogger === 'check-in'} onClick={() => setActiveLogger('check-in')} icon={Brain} label="Now" />
                <LoggerButton active={activeLogger === 'water'} onClick={() => setActiveLogger('water')} icon={Droplets} label="Water" />
                <LoggerButton active={activeLogger === 'sleep'} onClick={() => setActiveLogger('sleep')} icon={Moon} label="Sleep" />
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-4">
            {activeLogger === 'check-in' && (
              <div className="grid gap-4">
                <div className="grid gap-3 sm:grid-cols-4">
                  <ScoreInput label="P energy" value={productivityEnergy} onChange={setProductivityEnergy} />
                  <ScoreInput label="RJ energy" value={rejuvenationEnergy} onChange={setRejuvenationEnergy} />
                  <ScoreInput label="Resistance" value={resistance} onChange={setResistance} />
                  <ScoreInput label="Hydration" value={checkHydration} onChange={setCheckHydration} />
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <Field label="Emotion" value={emotion} onChange={setEmotion} placeholder="Focused, flat, restless..." />
                  <Field label="Body" value={bodyState} onChange={setBodyState} placeholder="Tense, light, tired..." />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="checkin-text">Note</Label>
                  <Textarea id="checkin-text" value={checkInText} onChange={event => setCheckInText(event.target.value)} placeholder="What is true right now?" />
                </div>
                <Button onClick={logCheckIn} disabled={isSaving}>
                  {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}
                  Log current state
                </Button>
              </div>
            )}

            {activeLogger === 'water' && (
              <div className="grid gap-4">
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                  <Button variant="outline" onClick={() => logWater('1', 'glass')} disabled={isSaving}>Glass</Button>
                  <Button variant="outline" onClick={() => logWater('1', 'bottle')} disabled={isSaving}>Bottle</Button>
                  <Button variant="outline" onClick={() => logWater('8', 'oz')} disabled={isSaving}>8 oz</Button>
                  <Button variant="outline" onClick={() => logWater('500', 'ml')} disabled={isSaving}>500 ml</Button>
                </div>
                <div className="grid gap-3 sm:grid-cols-[1fr_150px_1fr]">
                  <Field label="Amount" value={waterAmount} onChange={setWaterAmount} type="number" />
                  <div className="grid gap-2">
                    <Label>Unit</Label>
                    <Select value={waterUnit} onValueChange={(value: WaterUnit) => setWaterUnit(value)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="glass">Glass</SelectItem>
                        <SelectItem value="bottle">Bottle</SelectItem>
                        <SelectItem value="oz">Ounces</SelectItem>
                        <SelectItem value="ml">Milliliters</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <ScoreInput label="Hydration feeling" value={hydrationFeeling} onChange={setHydrationFeeling} />
                </div>
                <Field label="Note" value={waterNote} onChange={setWaterNote} placeholder="Optional" />
                <Button onClick={() => logWater()} disabled={isSaving}>
                  {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Droplets className="mr-2 h-4 w-4" />}
                  Log water
                </Button>
              </div>
            )}

            {activeLogger === 'sleep' && (
              <div className="grid gap-4">
                <div className="grid gap-3 sm:grid-cols-2">
                  <Field label="Sleep start" value={sleepStart} onChange={setSleepStart} type="datetime-local" />
                  <Field label="Wake time" value={sleepEnd} onChange={setSleepEnd} type="datetime-local" />
                </div>
                <div className="grid gap-3 sm:grid-cols-3">
                  <ScoreInput label="Quality" value={sleepQuality} onChange={setSleepQuality} />
                  <ScoreInput label="Restfulness" value={sleepRestfulness} onChange={setSleepRestfulness} />
                  <Field label="Interruptions" value={sleepInterruptions} onChange={setSleepInterruptions} type="number" />
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <ScoreInput label="Wake P" value={wakeP} onChange={setWakeP} />
                  <ScoreInput label="Wake RJ" value={wakeRj} onChange={setWakeRj} />
                </div>
                <Field label="Note" value={sleepNote} onChange={setSleepNote} placeholder="Optional" />
                <Button onClick={logSleep} disabled={isSaving}>
                  {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <BedDouble className="mr-2 h-4 w-4" />}
                  Log sleep
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </section>

      <section className="grid gap-3 lg:grid-cols-3">
        <HistoryPanel title="Water" icon={Droplets} rows={waterLogs.map(log => ({
          id: log.id,
          primary: `${Math.round(log.amount_ml)} ml`,
          secondary: [log.hydration_feeling != null ? `feel ${log.hydration_feeling}/10` : null, log.note].filter(Boolean).join(' | '),
          time: log.occurred_at,
        }))} />
        <HistoryPanel title="Sleep" icon={BedDouble} rows={sleepLogs.map(log => ({
          id: log.id,
          primary: formatSleep(log.duration_minutes),
          secondary: [`quality ${emptyDash(log.quality)}`, `RJ ${emptyDash(log.wake_rejuvenation_energy)}`].join(' | '),
          time: log.occurred_at,
        }))} />
        <HistoryPanel title="Check-ins" icon={Brain} rows={checkIns.map(log => ({
          id: log.id,
          primary: `P ${emptyDash(log.productivity_energy)} / RJ ${emptyDash(log.rejuvenation_energy)}`,
          secondary: [log.emotion_label, log.body_state, log.raw_text].filter(Boolean).join(' | '),
          time: log.occurred_at,
        }))} />
      </section>
    </div>
  );
}

/** Display a current state metric with its category color; an absent value is shown as a dash rather than inferred. */
function MetricTile({ label, value, tone }: { /** Visible caption describing this control or metric. */
label: string; /** Preformatted metric value displayed without additional measurement. */
value: string | number | null | undefined; /** Presentation color for the metric category. */
tone: 'cyan' | 'emerald' | 'blue' | 'amber' }) {
  const toneClass = {
    cyan: 'text-cyan-600 dark:text-cyan-300',
    emerald: 'text-emerald-600 dark:text-emerald-300',
    blue: 'text-blue-600 dark:text-blue-300',
    amber: 'text-amber-600 dark:text-amber-300',
  }[tone];

  return (
    <div className="rounded-md border bg-background p-3">
      <div className="text-xs font-medium text-muted-foreground">{label}</div>
      <div className={`mt-2 text-2xl font-semibold ${toneClass}`}>{value ?? '-'}</div>
    </div>
  );
}

/** Select a state-log form and visually mark the currently active category; the parent owns the selected form. */
function LoggerButton({ active, onClick, icon: Icon, label }: { /** Whether this log category is currently selected. */
active: boolean; /** Ask the owner to select this log category. */
onClick: () => void; /** Icon component rendered beside the visible caption. */
icon: typeof Brain; /** Visible caption describing this control or metric. */
label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex h-9 items-center justify-center gap-1 rounded text-xs font-medium transition-colors ${
        active ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
      }`}
    >
      <Icon className="h-3.5 w-3.5" />
      {label}
    </button>
  );
}

/** Adapt the shared state-log field to a 0–10 numeric input. Keep the draft as text so an empty observation remains distinct from zero. */
function ScoreInput({ label, value, onChange }: { /** Visible caption describing this control or metric. */
label: string; /** Controlled field draft, kept as a string until the enclosing form parses and validates it. */
value: string; /** Report the edited draft value to the owner; this callback does not itself prove a saved server result. */
onChange: (value: string) => void }) {
  return <Field label={label} value={value} onChange={onChange} type="number" min={0} max={10} />;
}

/** Associate a state-log input with a label-derived ID and report its raw string value. The enclosing form performs parsing and API persistence. */
function Field({
  label,
  value,
  onChange,
  placeholder,
  type = 'text',
  min,
  max,
}: {
  /** Visible caption describing this control or metric. */
  label: string;
  /** Raw draft text, including an empty string before an observation is supplied. */
  value: string;
  /** Replace the field draft with the raw input string. */
  onChange: (value: string) => void;
  /** Optional hint shown while the draft is empty. */
  placeholder?: string;
  /** HTML input type; defaults to text. */
  type?: string;
  /** Optional lower HTML input bound; the form still validates its payload. */
  min?: number;
  /** Optional upper HTML input bound; the form still validates its payload. */
  max?: number;
}) {
  const id = label.toLowerCase().replace(/[^a-z0-9]+/g, '-');
  return (
    <div className="grid gap-2">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        type={type}
        min={min}
        max={max}
        value={value}
        onChange={event => onChange(event.target.value)}
        placeholder={placeholder}
      />
    </div>
  );
}

/** Render the first five supplied state-log summaries, formatted in local time. An empty collection shows the no-logs state. */
function HistoryPanel({ title, icon: Icon, rows }: { /** Visible heading for this panel or collection. */
title: string; /** Icon component rendered beside the visible caption. */
icon: typeof Droplets; /** Preformatted history records in display order; the first five are shown. */
rows: Array<{ /** Stable observation identifier used as the history row key. */ id: string; /** Preformatted main observation summary. */ primary: string; /** Preformatted supporting observation details. */ secondary: string; /** Observation timestamp formatted into the user's local time for display. */ time: string }> }) {
  return (
    <Card>
      <CardHeader className="border-b pb-4">
        <CardTitle className="flex items-center gap-2 text-base">
          <Icon className="h-4 w-4 text-primary" />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {rows.length === 0 ? (
          <div className="p-4 text-sm text-muted-foreground">No logs yet.</div>
        ) : (
          <div className="divide-y">
            {rows.slice(0, 5).map(row => (
              <div key={row.id} className="grid gap-1 p-4">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium">{row.primary}</span>
                  <Badge variant="secondary" className="whitespace-nowrap text-[10px]">
                    {formatTime(row.time)}
                  </Badge>
                </div>
                {row.secondary && <div className="line-clamp-2 text-xs text-muted-foreground">{row.secondary}</div>}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/** Parse a state-log draft to a finite number, preserving blank or invalid input as null. */
function toOptionalNumber(value: string): number | null {
  if (value === '') return null;
  const number = Number(value);
  if (!Number.isFinite(number)) return null;
  return number;
}

/** Seed the sleep form at 23:00 on the previous local calendar day. */
function defaultSleepStart(): string {
  const date = new Date();
  date.setDate(date.getDate() - 1);
  date.setHours(23, 0, 0, 0);
  return toLocalInputValue(date);
}

/** Seed the sleep form at 07:00 on the current local calendar day. */
function defaultSleepEnd(): string {
  const date = new Date();
  date.setHours(7, 0, 0, 0);
  return toLocalInputValue(date);
}

/** Convert a valid Date into local datetime-input text without changing the intended wall-clock fields. */
function toLocalInputValue(date: Date): string {
  const offset = date.getTimezoneOffset();
  const local = new Date(date.getTime() - offset * 60000);
  return local.toISOString().slice(0, 16);
}

/** Convert a local datetime draft to an ISO instant; invalid dates throw and must be handled by the submission flow. */
function toIsoFromLocalInput(value: string): string {
  return new Date(value).toISOString();
}

/** Display sleep minutes as hours plus remaining minutes; absent or zero duration displays a dash. */
function formatSleep(minutes?: number | null): string {
  if (!minutes) return '-';
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours}h ${mins}m`;
}

/** Format a supplied timestamp as a browser-local hour/minute label. */
function formatTime(value: string): string {
  return new Date(value).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

/** Display an observed score against the ten-point scale, keeping null/undefined as a dash. */
function emptyDash(value: number | null | undefined): string {
  return value == null ? '-' : `${value}/10`;
}
